import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { AvatarModule } from 'primeng/avatar';

// Data Grid and related imports
import { NoComponentComponent,DataGridComponent } from '@fitness-force/grid';

// Models and services
import { ToastMessageService } from '@fitness-force/api';
import { ConfirmationService } from 'primeng/api';
import { CommissionPlanResponse, OwnershipLevel, StaffAssignmentItem, StaffAssignmentResponse } from '../../models';
import { CommissionPlanService } from '../../services';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { TENANTS_INFO, STAFF_STATE } from '@fitness-force/state';
import type { TENANTSINTERFACE, STAFFINTERFACE } from '@fitness-force/models';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { PlanNameComponent } from '../data-grid/plan-name/plan-name.component';
import { FrequencyTagComponent } from '../data-grid/frequency-tag/frequency-tag.component';
import { BooleanIconComponent } from '../data-grid/boolean-icon/boolean-icon.component';
import { StatusTagComponent } from '../data-grid/status-tag/status-tag.component';
import { ActionsComponent } from '@fitness-force/grid-utility/action';
import { DateFormatedComponent } from '@fitness-force/grid-utility';
import { DrawerModule } from 'primeng/drawer';
import { AuditTrailMiniComponent } from '@fitness-force/audit-trail';
import { FeatureFlagService } from '@fitness-force/shared-utils';

@Component({
  selector: 'lib-commission-plans-list',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SelectModule,
    DialogModule,
    AvatarModule,
    BreadcrumbComponent,
    DateTimeFormatPipe,
    ShortNamePipe,
    DataGridComponent,
    DrawerModule,
    AuditTrailMiniComponent
],
  providers: [ConfirmationService],
  template: `
  <div class="pt-3 md:px-4 px-2 pb-8 safe-area-margin">
    <h1 class="text-base md:text-lg text-900 flex align-items-center mb-0 line-height-1">
      {{ 'COMMISSION_MANAGEMENT.PLANS.TITLE' | translate }}
    </h1>
    <div class="mt-1 mb-3">
      <app-breadcrumb />
    </div>
  
    <div class="surface-0 border-round shadow-1 mt-3">
      <div class="px-2 md:px-3 pt-3">
        <!-- Header with Title and Create Button -->
        <div class="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center mb-3 gap-3">
          <div>
            <p class="text-500 mt-0 mb-0">{{ 'COMMISSION_MANAGEMENT.PLANS.SUBTITLE' | translate }}</p>
          </div>
          <p-button [label]="'COMMISSION_MANAGEMENT.PLANS.CREATE' | translate"
            icon="pi pi-plus"
            (onClick)="createPlan()"
            aria-label="create-commission-plan-button" />
        </div>
  
        <!-- Ownership Level Selector -->
        <div class="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center mb-3 gap-3">
          <div class="flex gap-2">
            <p-button [label]="'COMMISSION_MANAGEMENT.OWNERSHIP.COMPANY' | translate"
              [outlined]="selectedOwnershipLevel !== ownershipLevel.Company"
              [severity]="selectedOwnershipLevel === ownershipLevel.Company ? 'primary' : 'secondary'"
              (onClick)="selectOwnership(ownershipLevel.Company)"
              size="small"
              aria-label="company-level-button" />
            <p-button [label]="'COMMISSION_MANAGEMENT.OWNERSHIP.FRANCHISE' | translate"
              [outlined]="selectedOwnershipLevel !== ownershipLevel.FranchiseGroup"
              [severity]="selectedOwnershipLevel === ownershipLevel.FranchiseGroup ? 'primary' : 'secondary'"
              (onClick)="selectOwnership(ownershipLevel.FranchiseGroup)"
              size="small"
              aria-label="franchise-level-button" />
            <p-button [label]="'COMMISSION_MANAGEMENT.OWNERSHIP.TENANT' | translate"
              [outlined]="selectedOwnershipLevel !== ownershipLevel.Tenant"
              [severity]="selectedOwnershipLevel === ownershipLevel.Tenant ? 'primary' : 'secondary'"
              (onClick)="selectOwnership(ownershipLevel.Tenant)"
              size="small"
              aria-label="tenant-level-button" />
          </div>
  
          <!-- Filter Dropdown -->
          <div class="flex gap-2 align-items-center">
            <label for="filter-dropdown" class="text-sm font-semibold">{{ 'COMMISSION_MANAGEMENT.COMMON.FILTER' | translate }}:</label>
            <p-select
              [(ngModel)]="selectedFilter"
              [options]="filters"
              optionLabel="name"
              (onChange)="filterData()"
              [placeholder]="'COMMISSION_MANAGEMENT.COMMON.SELECT_FILTER' | translate"
              styleClass="w-12rem"
              aria-label="filter-dropdown">
              <ng-template let-item pTemplate="item">
                {{ 'COMMISSION_MANAGEMENT.FILTERS.' + item.value.toUpperCase() | translate }}
              </ng-template>
              <ng-template let-item pTemplate="selectedItem">
                {{ 'COMMISSION_MANAGEMENT.FILTERS.' + item.value.toUpperCase() | translate }}
              </ng-template>
            </p-select>
          </div>
        </div>
  
        <!-- Search Bar -->
        <div class="flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
          <p-iconField iconPosition="left" class="w-full md:w-20rem">
            <p-inputIcon styleClass="pi pi-search" />
            <input
              pInputText
              type="text"
              [placeholder]="'COMMISSION_MANAGEMENT.COMMON.SEARCH_PLACEHOLDER' | translate"
              [(ngModel)]="searchTerm"
              (ngModelChange)="onSearch()"
              class="w-full"
              aria-label="search-commission-plans-input" />
            </p-iconField>
          @if (showAuditLogsFeature) {
            <div class="hidden md:inline-block">
              <p-button (click)="showAuditTrail()" [label]="'Show Logs'" icon="fa fa-history" styleClass="text-sm p-button-text white-space-nowrap" aria-label="show-logs-button" />
            </div>
            <div class="md:hidden">
              <p-button (click)="showAuditTrail()" icon="fa fa-history" styleClass="p-button-outlined" aria-label="show-logs-button-mobile" />
            </div>
          }
          </div>
  
          <!-- Data Grid -->
          <app-data-grid
            [loading]="loading"
            [columnData]="tableData"
            [columns]="columns"
            [EmptyState]="emptyStateComponent$"
            [totalRecords]="totalRecordLength"
            [paginator]="true"
            [selectable]="selectable"
            [resizableColumns]="true"
            [columnResizeMode]="'expand'"
            (onAction)="onActionEvent($event)"
            data-testid="commission-plans-data-grid" />
        </div>
      </div>
    </div>
  
    <p-confirmDialog />
    <p-toast />
  
    <!-- Staff Assignment Dialog -->
    <p-dialog
      [visible]="showStaffDialog"
      [header]="'COMMISSION_MANAGEMENT.PLANS.ASSIGNED_STAFF' | translate"
      [modal]="true"
      [style]="{width: '50vw'}"
      [breakpoints]="{'960px': '75vw', '640px': '90vw'}"
      [draggable]="false"
      [resizable]="false">
  
      <div class="flex flex-column gap-4">
        <!-- Add Staff Section -->
        <div class="surface-50 border-round p-3">
          <h4 class="text-900 font-semibold mt-0 mb-3">{{ 'COMMISSION_MANAGEMENT.STAFF.ADD_STAFF' | translate }}</h4>
  
          <!-- Loading indicator when adding staff -->
          @if (addingStaff) {
            <div class="flex align-items-center gap-3 p-3 bg-primary-50 border-round mb-3">
              <i class="pi pi-spin pi-spinner text-primary text-2xl"></i>
              <div>
                <div class="font-semibold text-primary">{{ 'COMMISSION_MANAGEMENT.STAFF.ADDING_STAFF' | translate }}</div>
                <div class="text-sm text-600">{{ 'COMMISSION_MANAGEMENT.STAFF.PLEASE_WAIT' | translate }}</div>
              </div>
            </div>
          }
  
          <div class="flex gap-2">
            <p-select
              [(ngModel)]="selectedStaffId"
              [options]="availableStaff"
              optionLabel="name"
              optionValue="id"
              [placeholder]="'COMMISSION_MANAGEMENT.STAFF.SELECT_STAFF' | translate"
              [filter]="true"
              [showClear]="true"
              [disabled]="addingStaff"
              class="w-full"
              appendTo="body"
              (onChange)="onStaffSelect()"
              aria-label="select-staff-dropdown">
              <ng-template let-staff pTemplate="item">
                <div class="flex align-items-center gap-2">
                  @if (staff.ff_picture) {
                    <p-avatar [image]="staff.ff_picture"
                      size="normal"
                      shape="circle" />
                  }
                  @if (!staff.ff_picture) {
                    <p-avatar [label]="staff.name | shortName"
                      size="normal"
                      shape="circle" />
                  }
                  <div>
                    <div class="font-medium">{{ staff.name }}</div>
                    <div class="text-sm text-500">{{ staff.email_id }}</div>
                  </div>
                </div>
              </ng-template>
            </p-select>
          </div>
        </div>
  
        <!-- Assigned Staff List -->
        <div>
          <h4 class="text-900 font-semibold mt-0 mb-3">{{ 'COMMISSION_MANAGEMENT.STAFF.ASSIGNED_STAFF_LIST' | translate }} ({{ assignedStaff.length }})</h4>
  
          @if (loadingStaff) {
            <div class="flex justify-content-center p-4">
              <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
            </div>
          }
  
          @if (!loadingStaff && assignedStaff.length === 0) {
            <div class="text-center p-4 surface-50 border-round">
              <i class="pi pi-users text-4xl text-400 mb-3"></i>
              <p class="text-600 m-0">{{ 'COMMISSION_MANAGEMENT.STAFF.NO_ASSIGNED_STAFF' | translate }}</p>
            </div>
          }
  
          @if (!loadingStaff && assignedStaff.length > 0) {
            <div class="flex flex-column gap-2">
              @for (assignment of assignedStaff; track assignment) {
                <div
                  class="surface-50 border-round p-3 flex align-items-center justify-content-between">
                  <div class="flex align-items-center gap-3">
                    @if (getStaffById(assignment.staff_id)?.ff_picture) {
                      <p-avatar [image]="getStaffById(assignment.staff_id)?.ff_picture"
                        size="large"
                        shape="circle" />
                    }
                    @if (!getStaffById(assignment.staff_id)?.ff_picture) {
                      <p-avatar [label]="assignment.staff_name | shortName"
                        size="large"
                        shape="circle" />
                    }
                    <div>
                      <div class="font-semibold text-900">{{ assignment.staff_name }}</div>
                      <div class="text-sm text-600">{{ assignment.staff_email }}</div>
                      @if (assignment.staff_designation) {
                        <div class="text-sm text-500">{{ assignment.staff_designation }}</div>
                      }
                      <div class="flex gap-2 mt-1">
                        <p-tag [value]="assignment.staff_status"
                          [severity]="assignment.staff_status === 'Active' ? 'success' : 'warning'"
                          size="small" />
                        <p-tag [value]="'Assigned: ' + (assignment.assigned_at | datetimeformat:'defaultformat')"
                          severity="info"
                          size="small" />
                      </div>
                    </div>
                  </div>
                  <p-button icon="pi pi-trash"
                    [text]="true"
                    [rounded]="true"
                    severity="danger"
                    size="small"
                    (onClick)="removeStaff(assignment)"
                    [ariaLabel]="'remove-staff-' + assignment.staff_id" />
                </div>
              }
            </div>
          }
        </div>
      </div>
  
      <ng-template pTemplate="footer">
        <p-button [label]="'COMMISSION_MANAGEMENT.COMMON.CLOSE' | translate"
          (onClick)="showStaffDialog = false"
          [outlined]="true"
          aria-label="close-staff-dialog-button" />
      </ng-template>
    </p-dialog>

    @if (showAuditTrailSidebar && showAuditLogsFeature) {
      <p-drawer
        [visible]="showAuditTrailSidebar"
        position="right"
        [modal]="true"
        [appendTo]="'body'"
        [autoZIndex]="true"
        [blockScroll]="true"
        [showCloseIcon]="false"
        styleClass="w-full md:w-30rem"
        (onHide)="hideAuditTrail()"
        aria-label="audit-trail-sidebar"
        header="Activity Log"
        data-testid="audit-trail-sidebar">
        <ng-template pTemplate="content">
          <fitness-force-audit-trail-mini entityType="commission_plans"
            entityId=""
            [tenantId]="tenantId"
            [tenantData]="[]"
            [maxEvents]="10"
            [showStats]="true"
            [showFilters]="true"
            [showPagination]="true"
            height="100vh"
            title="Commission Plan Activity Log"
            [showCloseButton]="true"
            (closeEvent)="hideAuditTrail()"
            data-testid="audit-trail-mini-component" />
        </ng-template>
      </p-drawer>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommissionPlansListComponent implements OnInit, OnDestroy {
  private readonly commissionPlanService = inject(CommissionPlanService);
  private readonly toastService = inject(ToastMessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly featureFlagService = inject(FeatureFlagService);

  plans: CommissionPlanResponse[] = [];
  allPlans: CommissionPlanResponse[] = [];
  filteredPlans: CommissionPlanResponse[] = [];
  loading = false;
  totalRecords = 0;
  pageSize = 10;
  searchTerm = '';
  selectedOwnershipLevel: OwnershipLevel = OwnershipLevel.Company;
  tenantId = 0;
  tenantFranchiseId = 0;
  private readonly destroy$ = new Subject<void>();
  
  filters: any[];
  selectedFilter: {name: string, value: string} | undefined;

  // Data Grid properties
  tableData = new BehaviorSubject<CommissionPlanResponse[]>([]);
  columns = new BehaviorSubject<any[]>([]);
  emptyStateComponent$ = new BehaviorSubject<any>(NoComponentComponent);
  totalRecordLength = new BehaviorSubject({
    totalPage: 1,
    totalItems: 0,
    currentPage: 0
  });

  selectable = new BehaviorSubject<boolean>(false);

  // Staff assignment properties
  showStaffDialog = false;
  loadingStaff = false;
  addingStaff = false;
  selectedPlan: CommissionPlanResponse | null = null;
  assignedStaff: StaffAssignmentItem[] = [];
  allStaff: STAFFINTERFACE[] = [];
  selectedStaffId: number | null = null;

  showAuditTrailSidebar = false;
  showAuditLogsFeature = false;

  get ownershipLevel() {
    return OwnershipLevel;
  }

  // Get available staff excluding already assigned active staff
  get availableStaff(): STAFFINTERFACE[] {
    const assignedStaffIds = this.assignedStaff.map(a => a.staff_id);
    return this.allStaff.filter(staff => !assignedStaffIds.includes(staff.id));
  }

  constructor() {
    this.filters = [
      {name: 'ALL', value: 'all'},
      {name: 'ACTIVE', value: 'active'},
      {name: 'INACTIVE', value: 'inactive'},
    ];
  }

  ngOnInit() {
    this.featureFlagService.getFlag$('show_audit_trial').pipe(takeUntil(this.destroy$)).subscribe((isEnabled: boolean) => {
      this.showAuditLogsFeature = isEnabled;
      this.changeDetectorRef.detectChanges();
    });
    this.initializeColumns();
    // Restore previous state from service
    this.selectedOwnershipLevel = this.commissionPlanService.getSelectedOwnershipLevel();
    const savedFilter = this.commissionPlanService.getSelectedFilter();
    this.selectedFilter = savedFilter || this.filters.find(o => o.value === 'active');

    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tenant: TENANTSINTERFACE) => {
          this.tenantId = tenant.selected_tenant.id;
          this.tenantFranchiseId = tenant.selected_tenant.franchise_group_id;
          this.loadPlans();
        }
      });

    // Load staff list from store
    this.store.select(STAFF_STATE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (staffState: any) => {
          this.allStaff = staffState.staffListAll || [];
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  initializeColumns() {
    this.translate
      .get([
        'COMMISSION_MANAGEMENT.PLANS.NAME',
        'COMMISSION_MANAGEMENT.PLANS.CODE',
        'COMMISSION_MANAGEMENT.PLANS.FREQUENCY',
        'COMMISSION_MANAGEMENT.PLANS.START_DATE',
        'COMMISSION_MANAGEMENT.PLANS.END_DATE',
        'COMMISSION_MANAGEMENT.PLANS.CUMULATIVE',
        'COMMISSION_MANAGEMENT.PLANS.STATUS',
        'COMMISSION_MANAGEMENT.COMMON.ACTIONS'
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((translations) => {
        this.columns.next([
          {
            title: translations['COMMISSION_MANAGEMENT.PLANS.NAME'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'name_pri_lang',
            type: 'component',
            data: {
              name: PlanNameComponent,
              additionData: true,
              aData: {
                keyName: 'name_pri_lang',
                uri: `/admin/commission-management/plans/${this.selectedOwnershipLevel}/:id/rules`,
                canRedirect: true
              }
            }
          },
          {
            title: translations['COMMISSION_MANAGEMENT.PLANS.CODE'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'code',
            type: 'default'
          },
          {
            title: translations['COMMISSION_MANAGEMENT.PLANS.FREQUENCY'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'frequency',
            type: 'component',
            data: {
              name: FrequencyTagComponent,
              additionData: true
            }
          },
          {
            title: translations['COMMISSION_MANAGEMENT.PLANS.START_DATE'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'start_date',
            type: 'component',
            data: {
              name: DateFormatedComponent,
              additionData: true,
              aData: {
                KeyName: 'start_date',
                format: 'defaultformat'
              }
            }
          },
          {
            title: translations['COMMISSION_MANAGEMENT.PLANS.END_DATE'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'end_date',
            type: 'component',
            data: {
              name: DateFormatedComponent,
              additionData: true,
              aData: {
                KeyName: 'end_date',
                format: 'defaultformat'
              },
            },
          },
          {
            title: translations['COMMISSION_MANAGEMENT.PLANS.CUMULATIVE'],
            classes: 'py-2 white-space-nowrap text-center',
            dataProperty: 'is_cumulative',
            type: 'component',
            data: {
              name: BooleanIconComponent,
              additionData: true
            }
          },
          {
            title: translations['COMMISSION_MANAGEMENT.PLANS.STATUS'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'status',
            type: 'component',
            data: {
              name: StatusTagComponent,
              additionData: true
            }
          },
          {
            title: translations['COMMISSION_MANAGEMENT.COMMON.ACTIONS'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'actions',
            type: 'component',
            data: {
              name: ActionsComponent,
              additionData: true,
              aData: ['assign_staff', 'view', 'edit', 'delete']
            }
          }
        ]);
      });
  }

  selectOwnership(ownershipLevel: OwnershipLevel) {
    this.selectedOwnershipLevel = ownershipLevel;
    // Save state to service
    this.commissionPlanService.setSelectedOwnershipLevel(ownershipLevel);
    this.loadPlans();
  }

  loadPlans() {
    this.loading = true;
    this.commissionPlanService.getCommissionPlansByOwnership(
      this.selectedOwnershipLevel,
      this.tenantId,
      this.tenantFranchiseId
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: CommissionPlanResponse[]) => {
          this.allPlans = response || [];
          this.plans = response || [];
          this.initializeColumns();
          this.applyFilters();
          this.totalRecords = this.filteredPlans.length;
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          this.toastService.showError(error);
          this.loading = false;
          this.allPlans =  [];
          this.plans =  [];
          this.applyFilters();
          this.totalRecords = 0;
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  filterData() {
    // Save filter state to service
    this.commissionPlanService.setSelectedFilter(this.selectedFilter);
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.plans];

    // Apply status filter
    if (this.selectedFilter?.value === 'active') {
      filtered = filtered.filter(o => o.status === 1);
    } else if (this.selectedFilter?.value === 'inactive') {
      filtered = filtered.filter(o => o.status === 0);
    }

    // Apply search filter
    if (this.searchTerm) {
      filtered = filtered.filter(plan => 
        plan.name_pri_lang?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        plan.name_sec_lang?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        plan.code?.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }

    this.filteredPlans = filtered;
    this.totalRecords = filtered.length;
    
    // Update data grid
    this.tableData.next(this.filteredPlans);
    this.totalRecordLength.next({
      totalPage: 1,
      totalItems: this.totalRecords,
      currentPage: 0,
    });
    
    this.changeDetectorRef.detectChanges();
  }

  onSearch() {
    this.applyFilters();
  }

  createPlan() {
    this.router.navigate(['/admin/commission-management/plans/create']);
  }

  editPlan(plan: CommissionPlanResponse) {
    this.router.navigate(['/admin/commission-management/plans/edit', plan.id]);
  }

  viewRules(plan: CommissionPlanResponse) {
    this.router.navigate([
      '/admin/commission-management/plans',
      this.selectedOwnershipLevel,
      plan.id,
      'rules'
    ]);
  }

  deletePlan(plan: CommissionPlanResponse) {
    this.confirmationService.confirm({
      message: this.translate.instant('COMMISSION_MANAGEMENT.COMMON.CONFIRM_DELETE'),
      header: this.translate.instant('COMMISSION_MANAGEMENT.PLANS.DELETE'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('COMMISSION_MANAGEMENT.COMMON.YES'),
      rejectLabel: this.translate.instant('COMMISSION_MANAGEMENT.COMMON.NO'),
      accept: () => {
        this.commissionPlanService.deactivateCommissionPlanByOwnership(
          this.selectedOwnershipLevel,
          plan.id,
          this.tenantId,
          this.tenantFranchiseId
        )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.toastService.showSuccess(this.translate.instant('COMMISSION_MANAGEMENT.PLANS.DELETE_SUCCESS'));
              this.loadPlans();
            },
            error: (error) => {
              this.toastService.showError(error);
            }
          });
      }
    });
  }

  onActionEvent(event: any) {
    switch (event.action) {
      case 'assign_staff':
        this.openStaffAssignmentDialog(event.columnData);
        break;
      case 'view':
        this.viewRules(event.columnData);
        break;
      case 'edit':
        this.editPlan(event.columnData);
        break;
      case 'delete':
        this.deletePlan(event.columnData);
        break;
    }
  }

  // Staff Assignment Methods
  openStaffAssignmentDialog(plan: CommissionPlanResponse) {
    this.selectedPlan = plan;
    this.showStaffDialog = true;
    this.selectedStaffId = null;
    this.loadStaffAssignments();
  }

  loadStaffAssignments(reload = false) {
    if (!this.selectedPlan && !reload) return;

    this.loadingStaff = true;
    this.commissionPlanService.getStaffAssignments(this.selectedPlan?.id || 0)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: StaffAssignmentResponse) => {
          // Filter to show only active assignments
          this.assignedStaff = (response.items || []).filter(item => item.assignment_status === 'Active');
          this.loadingStaff = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          this.toastService.showError(error);
          this.loadingStaff = false;
          this.assignedStaff = [];
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  onStaffSelect() {
    if (!this.selectedPlan || !this.selectedStaffId) return;

    const planId = this.selectedPlan.id;
    const staffId = this.selectedStaffId;

    this.addingStaff = true;
    this.changeDetectorRef.detectChanges();

    this.commissionPlanService.assignStaff(planId, { staff_id: staffId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.showSuccess(this.translate.instant('COMMISSION_MANAGEMENT.STAFF.STAFF_ASSIGNED_SUCCESS'));
          this.selectedStaffId = null;
          this.addingStaff = false;
          this.loadStaffAssignments(true);
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          this.toastService.showError(error);
          this.selectedStaffId = null;
          this.addingStaff = false;
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  removeStaff(assignment: StaffAssignmentItem) {
    if (!this.selectedPlan) return;

    const planId = this.selectedPlan.id;
    this.confirmationService.confirm({
      message: this.translate.instant('COMMISSION_MANAGEMENT.STAFF.CONFIRM_REMOVE_STAFF'),
      header: this.translate.instant('COMMISSION_MANAGEMENT.COMMON.CONFIRM'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: this.translate.instant('COMMISSION_MANAGEMENT.COMMON.YES'),
      rejectLabel: this.translate.instant('COMMISSION_MANAGEMENT.COMMON.NO'),
      accept: () => {
        this.commissionPlanService.removeStaffAssignment(planId, assignment.assignment_id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.toastService.showSuccess(this.translate.instant('COMMISSION_MANAGEMENT.STAFF.STAFF_REMOVED_SUCCESS'));
              this.loadStaffAssignments(true);
              this.changeDetectorRef.detectChanges();
            },
            error: (error) => {
              this.toastService.showError(error);
            }
          });
      }
    });
  }

  getStaffById(staffId: number): STAFFINTERFACE | undefined {
    return this.allStaff.find(s => s.id === staffId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showAuditTrail(): void {
    if (this.showAuditLogsFeature) {
      this.showAuditTrailSidebar = true;
      this.changeDetectorRef.detectChanges();
    }
  }

  hideAuditTrail(): void {
    this.showAuditTrailSidebar = false;
    this.changeDetectorRef.detectChanges();
  }
}